import MaterialIcon from '@components/common/MaterialIcon';
import { VALID_BOT_LANGUAGES } from '@conversed/shared/src/central/constants/languages';
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import type React from 'react';
import { useTranslation } from 'react-i18next';

// 'bar' matches the flow editor's other language pickers (LanguageRichTextFields,
// BlockSliderForm) so a block's sidebar stays consistent. 'compact' is for the
// wide, light settings pages, where a full-width banner outweighs everything
// under it.
export type TLanguageSelectVariant = 'bar' | 'compact';

interface IProps {
  languages: string[];
  currentLanguage: string;
  onChange: (language: string) => void;
  variant?: TLanguageSelectVariant;
}

const getLanguageName = (languageCode: string) => {
  const matchingEntry = VALID_BOT_LANGUAGES.find(
    (entry) => entry[0] === languageCode,
  );

  return matchingEntry ? matchingEntry[1] : '';
};

export const LanguageSelect: React.FC<IProps> = ({
  languages,
  currentLanguage,
  onChange,
  variant = 'compact',
}) => {
  const { t } = useTranslation();
  const isBar = variant === 'bar';
  const selectLanguageLabel = t('flow.form.description.select.language.text');

  return (
    <div className={isBar ? 'relative w-full mb-2' : 'relative w-fit'}>
      <Listbox value={currentLanguage} onChange={onChange}>
        {({ open }) => (
          <>
            <ListboxButton
              aria-label={selectLanguageLabel}
              className={
                isBar
                  ? 'block px-2 py-2 text-xs w-full bg-primary-dark text-white'
                  : 'flex items-center gap-1 rounded-lg bg-primary-base px-3 py-1 text-xs font-medium text-white hover:bg-primary-dark'
              }>
              {isBar ? (
                `${selectLanguageLabel}: ${getLanguageName(currentLanguage)}`
              ) : (
                <>
                  <MaterialIcon
                    icon='translate'
                    classes={['text-sm leading-none text-white']}
                  />
                  {getLanguageName(currentLanguage)}
                  <MaterialIcon
                    icon={open ? 'expand_less' : 'expand_more'}
                    classes={['text-sm leading-none text-white']}
                  />
                </>
              )}
            </ListboxButton>
            {open && (
              <ListboxOptions
                static
                className={
                  isBar
                    ? 'absolute w-full z-10 mt-1 bg-white text-center shadow-lg'
                    : // Right-aligned so a long language name grows away from
                      // the edge rather than off it.
                      'absolute right-0 z-10 mt-1 w-max min-w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg'
                }>
                {languages.map((language) => (
                  <ListboxOption
                    key={language}
                    value={language}
                    className={({ active }) => {
                      if (isBar) {
                        return `${
                          active
                            ? 'text-white bg-primary-dark'
                            : 'text-gray-800'
                        } w-full cursor-pointer select-none px-4 py-2 text-xs`;
                      }

                      return `${
                        active
                          ? 'bg-primary-lightest text-primary-base'
                          : 'text-gray-600'
                      } cursor-pointer select-none px-3 py-2 text-xs`;
                    }}>
                    {getLanguageName(language)}
                  </ListboxOption>
                ))}
              </ListboxOptions>
            )}
          </>
        )}
      </Listbox>
    </div>
  );
};

export default LanguageSelect;
